import { useEffect, useRef } from "react";
import { useChatStore } from "../stores/chat";
import { toast, useToast } from "../stores/toast";

/**
 * Polls `GET /api/activity/recent` every ~8s and emits a toast for each
 * new event the agent has logged. Lets the user see invisible background
 * work — KB ingestion, memory consolidation, wiki article creation, etc.
 *
 * Sprint α-bis-2 (2026-05-09): also tracks long-running tasks. When an
 * action ending in ``_started`` arrives we open a sticky progress toast
 * keyed by the correlation_id; the matching ``_completed`` / ``_failed``
 * / ``_skipped`` event flips it to a terminal state and the store's
 * 2.5s hold elapses before auto-dismiss. Lets the user SEE that heavy
 * autonomous work is running — daily cycle / EOD / memory consolidation
 * / evolution mining — instead of wondering why the app feels slow.
 *
 * Design notes:
 *  - Uses an in-component cursor (`sinceRef`) so we never re-show events.
 *    The server echoes its own clock back as `now`, which we adopt as the
 *    next `since` value to avoid clock-skew bugs across browser/server.
 *  - First poll uses the current wall-clock as the cursor so historical
 *    events from before the page loaded don't all flood in at once.
 *  - Categories filter is hard-coded to the ones a user actually cares
 *    about: knowledge changes, memory updates, system events, queue jobs.
 *    LLM errors are also surfaced because they tell the user *why* the
 *    agent stopped doing things.
 *  - Mount this hook ONCE at the app shell. Mounting twice would double
 *    every toast.
 */
export function useBackgroundActivityToasts(enabled = true) {
	const sinceRef = useRef<string>("");
	const seenIdsRef = useRef<Set<string>>(new Set());
	const firstRunRef = useRef(true);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		let timer: number | null = null;

		// Initial cursor = "now" so we don't show historical events on
		// page load. The first response's `now` field becomes the durable
		// cursor for subsequent polls.
		sinceRef.current = new Date().toISOString().replace(/\.\d+Z$/, "Z");

		const poll = async () => {
			if (cancelled) return;
			try {
				const params = new URLSearchParams({
					since: sinceRef.current,
					// Sprint α-bis-2: lower the importance floor to "low" so
					// we can pick up the started/completed/skipped events that
					// fire at low importance for non-spam progress tracking.
					// The toast renderer still filters via formatToast — events
					// it doesn't recognize stay silent, so dropping the floor
					// doesn't add noise.
					min_importance: "low",
					categories: "knowledge,memory,queue,system,llm,performance",
					limit: "30",
				});
				const res = await fetch(`/api/activity/recent?${params.toString()}`, {
					credentials: "same-origin",
				});
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const body = (await res.json()) as {
					events: ActivityEventDTO[];
					now: string;
				};
				if (!cancelled && body.events?.length) {
					if (!firstRunRef.current) {
						for (const ev of body.events) {
							const id = `${ev.timestamp}|${ev.category}|${ev.action}|${ev.summary}`;
							if (seenIdsRef.current.has(id)) continue;
							seenIdsRef.current.add(id);

							// Sprint α-bis-2 (2026-05-09): handle progress
							// pairs FIRST. A long-running task fires
							// `<task>_started` → optional `<task>_progress` →
							// terminal `<task>_completed`/`_failed`/`_skipped`.
							// progressKey is the correlation_id when present
							// (so cross-process started/completed pairs match);
							// falls back to the action prefix.
							const progressInfo = classifyProgressEvent(ev);
							if (progressInfo) {
								handleProgressEvent(ev, progressInfo);
								continue;
							}

							// Regular transient toast.
							const { kind, title } = formatToast(ev);
							if (title) toast[kind](title, ev.summary);
						}
						// Bound the dedup set so it can't grow unbounded
						if (seenIdsRef.current.size > 500) {
							seenIdsRef.current = new Set(
								Array.from(seenIdsRef.current).slice(-200),
							);
						}
					}
					firstRunRef.current = false;
				} else {
					firstRunRef.current = false;
				}
				if (body.now) sinceRef.current = body.now;
			} catch {
				// Network blip — ignore. Polling will retry on the next tick.
			}
			if (!cancelled) {
				timer = window.setTimeout(poll, 8000);
			}
		};

		// Kick off after a short delay so we don't slam the server during
		// the initial page load alongside every other GET.
		timer = window.setTimeout(poll, 2000);

		return () => {
			cancelled = true;
			if (timer !== null) window.clearTimeout(timer);
		};
	}, [enabled]);
}

interface ActivityEventDTO {
	category: string;
	action: string;
	summary: string;
	details: Record<string, unknown>;
	timestamp: string;
	importance: "low" | "medium" | "high";
	correlation_id: string;
}

type ToastKind = "success" | "error" | "info" | "warning";

// ── Sprint α-bis-2: progress pair detection ─────────────────────────────

interface ProgressEventInfo {
	progressKey: string;
	stage: "started" | "progress" | "completed" | "failed" | "skipped";
	displayTitle: string;
}

// Known long-running task names — anything matching one of these prefixes
// (combined with a `_started`/`_progress`/`_completed`/`_failed`/`_skipped`
// suffix) opens a sticky progress toast. Adding a new long-running task
// requires emitting the matching event pair on the backend AND adding
// the prefix here so the UI knows to render it as progress.
const LONG_RUNNING_PREFIXES: Array<{ prefix: string; title: string }> = [
	{ prefix: "ingest", title: "Ingesting" },
	{ prefix: "compile", title: "Compiling wiki" },
	{ prefix: "wiki_compile", title: "Compiling wiki" },
	{ prefix: "synthesize", title: "Synthesizing" },
	{ prefix: "research", title: "Researching" },
	{ prefix: "daily_cycle_briefing", title: "Generating morning briefing" },
	{ prefix: "daily_cycle_eod", title: "Generating EOD report" },
	{ prefix: "daily_cycle_weekly", title: "Generating weekly reflection" },
	{ prefix: "daily_cycle_editor", title: "Running editor pass" },
	{ prefix: "daily_cycle_fact-checker", title: "Running fact-checker pass" },
	{ prefix: "daily_reflection", title: "Generating daily reflection" },
	{ prefix: "weekly_reflection", title: "Generating weekly reflection" },
	{ prefix: "memory_consolidate", title: "Consolidating memory" },
	{ prefix: "memory_compact", title: "Compacting memory" },
	{ prefix: "memory_to_wiki", title: "Memory → Wiki bridge" },
	{ prefix: "wiki_to_memory", title: "Wiki → Memory bridge" },
	{ prefix: "evolution_mine", title: "Mining reflexes" },
	{ prefix: "heartbeat_goal_pursuit", title: "Pursuing goals" },
	{ prefix: "onenote_import", title: "Importing OneNote" },
	// m365_mail_import_all is listed FIRST so the multi-account run matches
	// its own title before the single-account prefix's startsWith() catches it.
	{ prefix: "m365_mail_import_all", title: "Importing from all Microsoft 365 accounts" },
	{ prefix: "m365_mail_import", title: "Importing Microsoft 365 emails" },
	{ prefix: "embedding_model_install", title: "Installing embedding model" },
];

const STAGE_SUFFIXES: Array<{
	suffix: string;
	stage: ProgressEventInfo["stage"];
}> = [
	{ suffix: "_started", stage: "started" },
	{ suffix: "_progress", stage: "progress" },
	{ suffix: "_completed", stage: "completed" },
	{ suffix: "_failed", stage: "failed" },
	{ suffix: "_skipped", stage: "skipped" },
	{ suffix: "_cancelled", stage: "skipped" },
	{ suffix: "_quarantined", stage: "failed" },
];

function classifyProgressEvent(ev: ActivityEventDTO): ProgressEventInfo | null {
	const a = ev.action;
	for (const { suffix, stage } of STAGE_SUFFIXES) {
		if (!a.endsWith(suffix)) continue;
		const taskRoot = a.slice(0, -suffix.length);
		const match = LONG_RUNNING_PREFIXES.find(
			(p) => taskRoot === p.prefix || taskRoot.startsWith(`${p.prefix}_`),
		);
		if (!match) return null;

		// progressKey = correlation_id when present (so distributed
		// started/completed pairs match across processes / threads),
		// otherwise the task root + timestamp so distinct
		// concurrent runs don't collide on a single sticky toast.
		const progressKey = ev.correlation_id
			? `${match.prefix}::${ev.correlation_id}`
			: stage === "started"
				? `${match.prefix}::${ev.timestamp}`
				: `${match.prefix}`;

		return {
			progressKey,
			stage,
			displayTitle: match.title,
		};
	}
	return null;
}

function handleProgressEvent(
	ev: ActivityEventDTO,
	info: ProgressEventInfo,
): void {
	const { progressKey, stage, displayTitle } = info;
	const summary = ev.summary?.trim() || "";

	// P4.7 (2026-05-19) — publish to chat store IN ADDITION to the corner
	// toaster. The chat store's setMessageProgress no-ops when no message
	// matches the correlation_id, so events for background-only work
	// (e.g. heartbeat-driven evolution mining with no associated chat
	// turn) flow only to the toaster. Events for tools running inside a
	// live chat turn publish to BOTH surfaces — the corner toaster + the
	// InlineProgress sub-component under the message bubble.
	publishToChatStore(ev, stage);

	if (stage === "started") {
		toast.progress(progressKey, displayTitle, {
			message: summary || undefined,
			phase: extractPhase(ev),
			progress: extractProgress(ev),
			kind: "info",
		});
		return;
	}

	if (stage === "progress") {
		// Update an existing toast IF one is open for this key.
		// Without a started event first there's no toast to update —
		// avoid spawning one mid-stream (would carry no startedAt for
		// the elapsed timer).
		const existing = useToast.getState().toasts.find(
			(t) => t.progressKey === progressKey,
		);
		if (!existing) return;
		toast.progress(progressKey, displayTitle, {
			message: summary || undefined,
			phase: extractPhase(ev),
			progress: extractProgress(ev),
			kind: "info",
		});
		return;
	}

	// Terminal — flip to done. If no started event was seen (e.g. the
	// page loaded after the task started), spawn a brief terminal
	// toast as a regular transient so the user still sees the outcome.
	const existing = useToast.getState().toasts.find(
		(t) => t.progressKey === progressKey,
	);
	if (existing) {
		toast.progressDone(progressKey, stage as "completed" | "failed" | "skipped", {
			message: summary || undefined,
		});
		return;
	}

	// No sticky toast to flip — emit a transient toast so the terminal
	// state isn't silent.
	const kind: ToastKind =
		stage === "completed" ? "success" : stage === "failed" ? "error" : "warning";
	toast[kind](displayTitle, summary || undefined);
}

// P4.7 (2026-05-19) — bridge from activity events to the chat-store's
// inline-progress slot. When an event carries a correlation_id that
// matches the latest assistant message, publish phase + startedAt so
// MessageBubble can render the InlineProgress sub-component beneath
// the bubble. No-op when correlation_id is empty OR no message matches
// (silently drops events for background-only work).
function publishToChatStore(
	ev: ActivityEventDTO,
	stage: "started" | "progress" | "completed" | "failed" | "skipped" | "cancelled" | "quarantined",
): void {
	const corrId = ev.correlation_id?.trim();
	if (!corrId) return;
	try {
		const setMessageProgress = useChatStore.getState().setMessageProgress;
		const isTerminal =
			stage === "completed" ||
			stage === "failed" ||
			stage === "skipped" ||
			stage === "cancelled" ||
			stage === "quarantined";
		if (isTerminal) {
			setMessageProgress(corrId, null);
			return;
		}
		// "started" / "progress" — surface phase text. extractPhase reads
		// details.phase / step / current_step / current and falls back to
		// the event's summary if no structured phase is present.
		const phase =
			extractPhase(ev) ||
			ev.summary?.trim() ||
			"Working…";
		// For "started" events, use the event's timestamp (epoch ms from
		// the server) as the elapsed-clock anchor when parseable; fall
		// back to client wall-clock. For "progress" events the existing
		// startedAt in the store is preserved (setMessageProgress
		// honors the prior value unless explicitly overridden).
		let startedAt: number | undefined;
		if (stage === "started") {
			const t = Date.parse(ev.timestamp);
			startedAt = Number.isFinite(t) ? t : Date.now();
		}
		setMessageProgress(corrId, phase, startedAt);
	} catch {
		// Defensive: chat store missing / store error MUST NEVER break
		// the toaster path. P4.7 is additive UX polish on top of the
		// existing corner toaster — the toaster keeps working even when
		// the chat-store branch fails.
	}
}

function extractPhase(ev: ActivityEventDTO): string | undefined {
	const d = ev.details;
	const candidates = ["phase", "step", "current_step", "current"];
	for (const k of candidates) {
		const v = d?.[k];
		if (typeof v === "string" && v.trim()) return v.trim();
	}
	return undefined;
}

function extractProgress(ev: ActivityEventDTO): number | undefined {
	const d = ev.details;
	// Direct fraction (0..1)
	const direct = d?.progress_ratio ?? d?.progress;
	if (typeof direct === "number" && Number.isFinite(direct)) {
		return Math.max(0, Math.min(1, direct));
	}
	// Discrete progress as N/total
	const processed = d?.pages_processed ?? d?.processed ?? d?.completed;
	const total = d?.pages_total ?? d?.total;
	if (
		typeof processed === "number" &&
		typeof total === "number" &&
		total > 0
	) {
		return Math.max(0, Math.min(1, processed / total));
	}
	return undefined;
}

function formatToast(ev: ActivityEventDTO): { kind: ToastKind; title: string } {
	// Map (category, action) → user-facing title + kind. Anything we don't
	// know about returns an empty title which suppresses the toast — we
	// deliberately err toward silence rather than spam unrecognized events.
	const c = ev.category;
	const a = ev.action;

	if (c === "llm" && (a === "error" || a === "failed" || a === "exhausted")) {
		return { kind: "error", title: "LLM error" };
	}
	if (c === "knowledge") {
		if (a === "ingested") return { kind: "success", title: "Knowledge ingested" };
		if (a === "compiled" || a === "wiki_compiled")
			return { kind: "success", title: "Wiki updated" };
		if (a === "synthesized") return { kind: "success", title: "Synthesis created" };
		if (a === "researched") return { kind: "success", title: "Research saved" };
		// P5.6 follow-up (2026-05-17) — search backend events. The "used"
		// event fires on every web_search/research call (high frequency)
		// so it's deliberately omitted — it lands in /activity for
		// retroactive browsing but doesn't toast. The medium-importance
		// "fallback" + "failed" events DO toast — the user opted into
		// SearXNG explicitly and should know when it didn't serve their
		// query as configured.
		if (a === "search_backend_fallback") {
			return { kind: "warning", title: "Search fallback to DDG" };
		}
		if (a === "search_backend_failed") {
			return { kind: "error", title: "Search failed" };
		}
		// 2026-05-22: chat-ask parked — KB ingest detected the file is a
		// meta-prompt addressed at the agent (e.g. "could you analyze
		// this framework"), parked it to library/parked-chat-asks/ with
		// a sidecar, and skipped the classify LLM call. Warning so the
		// user knows to paste it into chat instead.
		if (a === "chat_ask_parked")
			return { kind: "warning", title: "Inbox file looks like a chat ask" };
		// "extracted" deliberately omitted — fires on every chat turn, too noisy
	}
	if (c === "memory") {
		if (a === "consolidated") return { kind: "success", title: "Memory updated" };
		if (a === "compacted") return { kind: "success", title: "Memory compacted" };
		if (a === "approved") return { kind: "success", title: "Memory approved" };
		// Phase 6: dream phases
		if (a === "light_dream_completed") return { kind: "info", title: "Light dream captured" };
		if (a === "deep_dream_completed") return { kind: "success", title: "Deep dream consolidated" };
		if (a === "rem_dream_completed") return { kind: "success", title: "REM dream integrated" };
		if (a === "light_dream_skipped" || a === "deep_dream_skipped" || a === "rem_dream_skipped")
			return { kind: "warning", title: "Dream phase skipped" };
		if (a === "deep_dream_contradiction_surfaced")
			return { kind: "warning", title: "Memory contradiction found" };
		if (a === "predict_generated") return { kind: "info", title: "Predictions generated" };
		if (a === "predict_validated") return { kind: "info", title: "Predictions validated" };
	}
	if (c === "queue") {
		if (a === "drained") return { kind: "success", title: "Work queue drained" };
		if (a === "failed") return { kind: "warning", title: "Background job failed" };
	}
	if (c === "system") {
		if (a === "llm_recovered") return { kind: "success", title: "LLM back online" };
		if (a === "llm_lost") return { kind: "error", title: "LLM offline" };
		if (a === "update_available") return { kind: "info", title: ev.summary || "Update available" };
		if (a === "pairing_request") return { kind: "warning", title: "New contact wants to message you" };
		// Error-observability hooks. First-occurrence CRITICAL surfaces
		// immediately so the user can triage at /logs; auto-reopen on
		// recurrence surfaces because "we thought this was fixed but it
		// came back" is a meaningful signal.
		if (a === "error_log_new_critical")
			return { kind: "error", title: ev.summary || "New critical error" };
		if (a === "error_log_reopened")
			return { kind: "warning", title: ev.summary || "Error auto-reopened" };
	}

	// Unknown event — silent.
	return { kind: "info", title: "" };
}
